import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID } from 'services/wp';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import AlertError from 'components/AlertError';
import validateEmail from 'utils/validateEmail';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';

const GetUpdatesForm = ({ setOpen }) => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (e) => {
        setEmail(e.target.value);
    };

    //!TODO HANDLING DISCLAIMER
    const [agreeToTerms, setAgreeToTerms] = useState(false);

    const handleAgreeToTerms = (e) => {
        const { name, type, checked } = e.target;

        // Additional handling for checkbox
        if (type === 'checkbox' && name === 'agreeToTerms') {
            setAgreeToTerms(checked);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        const missing = [];
        if (!email?.trim()) {
            missing.push('Email');
        } else if (!validateEmail(email)) {
            missing.push('Email (Invalid format)');
        }
        if (!agreeToTerms) missing.push('Terms & Conditions Agreement');

        if (missing.length > 0) {
            return;
        }

        setIsLoading(true);  // Start loading
        setShowErrorMessage(false);

        // Convert form data to JSON object
        const jsonData = {
            email: email
        };


        try {
            await withSubmissionTimeout(async () => {
                const accountID = await getAccountID();
                const webhookID = await getWebhookID({ formName: "get_updates" });

                await sendToGHL(jsonData, accountID, webhookID);
            });

            // Show success message
            setShowSuccessMessage(true);
            // Hide success message after 2 seconds
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 2000);

            // Reset form after submission
            setAgreeToTerms(false);
            setSubmitted(false);
            setEmail("");

            // Close the modal after a short delay
            setTimeout(() => {
                setOpen(false);
                document.body.style.overflow = "auto";
            }, 1000);

        } catch (error) {
            // Show error message
            setErrorMessage(getSubmissionErrorMessage(error));
            setShowErrorMessage(true);

            setAgreeToTerms(false)

        } finally {
            // Stop loading after submission, whether successful or failed
            setIsLoading(false);
        }
    };

    const emailEmpty = submitted && !email?.trim();
    const emailFormatInvalid = submitted && Boolean(email?.trim()) && !validateEmail(email);
    const emailInvalid = emailEmpty || emailFormatInvalid;
    const termsInvalid = submitted && !agreeToTerms;

    return (
        <>
            <form onSubmit={handleSubmit} noValidate className="flex flex-col items-center gap-y-2">
                {showSuccessMessage && (
                    <div className="toast toast-center toast-middle z-[9999]">
                        <div className="alert alert-success text-base-100">
                            <span>Message sent successfully.</span>
                        </div>
                    </div>
                )}
                <AlertError showError={showErrorMessage} onClose={() => setShowErrorMessage(false)}>
                    {errorMessage || 'Something went wrong. Please try again later.'}
                </AlertError>

                <div className="w-full relative" data-invalid={emailInvalid ? "true" : undefined}>
                    {emailInvalid && (
                        <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                            {emailEmpty ? 'Required' : 'Invalid Email'}
                        </div>
                    )}
                    <input
                        type="email"
                        className={`input input-bordered w-full text-primary ${emailInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        placeholder='Your Email *'
                        value={email}
                        onChange={handleInputChange}
                    />
                </div>


                {/* DISCLAIMER */}
                <label
                    className={`label cursor-pointer flex items-start rounded-lg p-2 ${termsInvalid ? 'border-2 border-red-600 bg-red-50' : ''}`}
                    data-invalid={termsInvalid ? "true" : undefined}
                >
                    <input
                        type="checkbox"
                        className={`checkbox rounded mr-4 mt-1 ${termsInvalid ? 'checkbox-error border-red-600' : ''}`}
                        name="agreeToTerms"
                        checked={agreeToTerms}
                        onChange={handleAgreeToTerms}
                    />
                    <span className="label-text text-xs">
                        {termsInvalid && (
                            <span className="inline-block text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded mr-2 mb-1">
                                Required: Please check to agree
                            </span>
                        )}
                        By providing your email address, you agree to receive email messages from Insurance Enterprise USA
                        about our products and services. Email HELP to
                        <span className='underline text-blue-600 hover:text-blue-400 px-1'>
                            <a href='mailto:benefits@insuranceenterpriseusa.com' rel='noreferrer'>
                                benefits@insuranceenterpriseusa.com
                            </a>
                        </span>
                        for assistance. You can unsubscribe at any time
                    </span>
                </label>

                <button type="submit" className={`btn w-full text-base-100 btn-secondary ${isLoading ? 'btn-disabled' : ''}`} disabled={isLoading}>
                    {isLoading ? <span className="loading loading-spinner loading-lg text-secondary"></span> : 'Get Updates'}
                </button>


                {/* T&C and Privacy Policy */}
                <div className='flex justify-center underline text-blue-600 text-xs'>
                    <NavLink className='px-2' to='/terms-and-condition' target='_blank'>Terms and Conditions</NavLink>
                    <NavLink className='px-2' to='/privacy-policy' target='_blank'>Privacy Policy</NavLink>
                </div>
            </form>
        </>
    );
}

export default GetUpdatesForm;
